/**
 * image.js
 * Making a picture something Instagram will actually accept, in the browser.
 *
 * Two things stop an ordinary image publishing, and neither is about how many
 * files you have:
 *
 *   **Instagram accepts JPEG and nothing else.** A PNG uploads perfectly well,
 *   sits in the library looking fine, and is refused at publish time. So is a
 *   WebP, an AVIF, or a screenshot straight off a phone.
 *
 *   **Instagram accepts a narrow range of shapes** — between 0.79 and 1.92
 *   wide-to-tall. A tall screenshot, a wide banner or a square-ish crop that is
 *   slightly out are all refused, again at publish time, after everything else
 *   has gone right.
 *
 * Both are fixed here rather than server-side. A Worker cannot decode a JPEG
 * without a paid image service, but every browser has had a canvas for fifteen
 * years — so the conversion happens on the machine that already has the file
 * open, costs nothing, and needs no new dependency.
 *
 * The geometry is separated from the drawing on purpose: `planConversion` is
 * ordinary arithmetic with no DOM in it, which is what makes the awkward cases
 * — a panorama, a one-pixel-out ratio, an image already fine — testable.
 *
 * Dependencies: none (canvas, at the bottom)
 */

/** What Instagram will take. Mirrors PLATFORMS.instagram in the Worker's config. */
export const INSTAGRAM = {
  mime: 'image/jpeg',
  minRatio: 0.79,
  maxRatio: 1.92,
  // Instagram downsizes anything larger anyway, so going above this only makes
  // the upload slower.
  maxEdge: 1440,
};

/** Formats a browser canvas can reliably read. */
export const READABLE = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif'];

export const FIT_MODES = {
  pad: {
    label: 'Add a border',
    hint: 'Keeps the whole picture and fills the gap with a colour. Nothing is lost.',
  },
  crop: {
    label: 'Crop to fit',
    hint: 'Trims from the edges, centred. Nothing is added, but the edges go.',
  },
};

/**
 * What has to be done to a picture, worked out before anything is drawn.
 *
 * @param {{width: number, height: number, type: string}} source
 * @param {{fit?: 'pad'|'crop', target?: object}} [options]
 * @returns {{
 *   needed: boolean, reasons: string[], convert: boolean, resize: boolean, reshape: boolean,
 *   width: number, height: number, drawWidth: number, drawHeight: number,
 *   offsetX: number, offsetY: number, ratio: number
 * }}
 */
export function planConversion(source, { fit = 'pad', target = INSTAGRAM } = {}) {
  const width = Math.max(1, Math.round(Number(source.width) || 0));
  const height = Math.max(1, Math.round(Number(source.height) || 0));
  const ratio = width / height;

  const reasons = [];
  const convert = source.type !== target.mime;
  if (convert) reasons.push(`${shortType(source.type)} becomes JPEG`);

  // ---- the shape ----
  //
  // Clamped to the nearest edge of the allowed range rather than snapped to a
  // fixed 4:5, so an image that is only slightly out changes as little as
  // possible. Squeezing a 1.95 panorama into 4:5 would throw away most of it
  // for no reason.
  const wanted = Math.min(target.maxRatio, Math.max(target.minRatio, ratio));
  const reshape = Math.abs(wanted - ratio) > 0.001;

  let canvasWidth = width;
  let canvasHeight = height;

  if (reshape) {
    if (fit === 'crop') {
      // Keep the shorter dimension, take from the longer one.
      if (ratio > wanted) canvasWidth = Math.round(height * wanted);
      else canvasHeight = Math.round(width / wanted);
    } else {
      // Pad: grow the shorter dimension so nothing is lost.
      if (ratio > wanted) canvasHeight = Math.round(width / wanted);
      else canvasWidth = Math.round(height * wanted);
    }
    reasons.push(
      `${ratio.toFixed(2)} is outside ${target.minRatio}–${target.maxRatio}, so it is ` +
        (fit === 'crop' ? 'cropped' : 'padded'),
    );
  }

  // ---- the size ----
  const longest = Math.max(canvasWidth, canvasHeight);
  const scale = longest > target.maxEdge ? target.maxEdge / longest : 1;
  const resize = scale < 1;
  if (resize) reasons.push(`resized to ${target.maxEdge}px on its longest edge`);

  const outWidth = Math.max(1, Math.round(canvasWidth * scale));
  const outHeight = Math.max(1, Math.round(canvasHeight * scale));

  // Where the picture sits on that canvas.
  let drawWidth;
  let drawHeight;

  if (fit === 'crop' && reshape) {
    // Fill the canvas, letting the overflow fall off the edges.
    const cover = Math.max(outWidth / width, outHeight / height);
    drawWidth = Math.round(width * cover);
    drawHeight = Math.round(height * cover);
  } else {
    // Fit inside it, leaving the padding visible.
    const contain = Math.min(outWidth / width, outHeight / height);
    drawWidth = Math.round(width * contain);
    drawHeight = Math.round(height * contain);
  }

  return {
    needed: convert || reshape || resize,
    reasons,
    convert,
    reshape,
    resize,
    ratio,
    width: outWidth,
    height: outHeight,
    drawWidth,
    drawHeight,
    offsetX: Math.round((outWidth - drawWidth) / 2),
    offsetY: Math.round((outHeight - drawHeight) / 2),
  };
}

/** "image/png" -> "PNG" */
export function shortType(mime) {
  return String(mime ?? '')
    .split('/')[1]
    ?.toUpperCase()
    .replace('JPEG', 'JPEG') ?? 'That file';
}

/** Whether a browser canvas can be expected to read this at all. */
export const isReadable = (type) => READABLE.includes(String(type));

/** "photo.png" -> "photo.jpg", leaving anything else alone. */
export function jpegName(name) {
  const base = String(name ?? 'image').replace(/\.[^.]+$/, '');
  return `${base || 'image'}.jpg`;
}

/**
 * A one-line account of what will happen, for the list before it happens.
 */
export function describePlan(plan) {
  if (!plan.needed) return 'Ready as it is.';
  return plan.reasons.join('; ');
}

/**
 * How to render a source at an exact size.
 *
 * Different from `planConversion`, which nudges a picture into an allowed
 * *range* of shapes and otherwise leaves it alone. This produces precisely the
 * dimensions asked for, because a Facebook link preview is 1200x630 and
 * nothing else will do.
 *
 * `fit` is the honest choice at the heart of it, and there is no right answer:
 *   pad   keeps the whole picture and fills the rest with a colour
 *   crop  fills the frame and loses the edges
 *
 * Upscaling is allowed here, unlike in `planConversion`, because a preset is a
 * requirement rather than a suggestion — but it is reported, so a picture that
 * will come out soft says so before it is made.
 *
 * @param {{width: number, height: number, type: string}} source
 * @param {{width: number, height: number}} target
 */
export function planExact(source, target, { fit = 'pad', mime = INSTAGRAM.mime } = {}) {
  const sourceWidth = Math.max(1, Math.round(Number(source.width) || 0));
  const sourceHeight = Math.max(1, Math.round(Number(source.height) || 0));

  const width = Math.max(1, Math.round(target.width));
  const height = Math.max(1, Math.round(target.height));

  // `contain` fits the whole thing inside; `cover` fills and overflows.
  const contain = Math.min(width / sourceWidth, height / sourceHeight);
  const cover = Math.max(width / sourceWidth, height / sourceHeight);
  const scale = fit === 'crop' ? cover : contain;

  const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
  const drawHeight = Math.max(1, Math.round(sourceHeight * scale));

  // How much of the source is lost, or how much of the frame is padding —
  // the number that tells you whether this preset suits this picture at all.
  const sourceRatio = sourceWidth / sourceHeight;
  const targetRatio = width / height;
  const mismatch = Math.abs(sourceRatio - targetRatio) / Math.max(sourceRatio, targetRatio);

  const reasons = [];
  if (source.type !== mime) {
    reasons.push(`${shortType(source.type)} becomes ${shortType(mime)}`);
  }
  if (mismatch > 0.01) {
    reasons.push(
      fit === 'crop'
        ? `${Math.round(mismatch * 100)}% is cropped away`
        : `padded — the picture keeps its shape inside the frame`,
    );
  }
  if (scale > 1.05) reasons.push(`enlarged ${scale.toFixed(1)}x, so it will look softer`);

  return {
    width,
    height,
    drawWidth,
    drawHeight,
    offsetX: Math.round((width - drawWidth) / 2),
    offsetY: Math.round((height - drawHeight) / 2),
    scale,
    mismatch,
    // Worth a warning rather than a refusal: sometimes losing a third of a
    // photograph is exactly what you meant.
    heavyCrop: fit === 'crop' && mismatch > 0.34,
    upscaled: scale > 1.05,
    reasons,
    mime,
  };
}

// ============================================
// THE DRAWING
// ============================================

/**
 * Reads a file's dimensions without decoding the whole thing twice.
 *
 * `createImageBitmap` is the fast path and handles every format the browser
 * knows. The `<img>` fallback exists for Safari versions that refuse a bitmap
 * from certain sources; both end up with the same numbers.
 */
export async function readImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return { bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      /* fall through to the <img> path */
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ bitmap: img, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name} could not be read as an image.`));
    };
    img.src = url;
  });
}

/**
 * Renders a source to an exact size and format.
 *
 * The generalisation of `prepareForInstagram`: any target, any output format.
 * A transparent source going to JPEG is painted onto the background colour
 * first, because JPEG has no transparency and the alternative is black.
 *
 * @param {File|ImageBitmap|HTMLImageElement} source
 * @returns {Promise<{file: File, plan: object}>}
 */
export async function renderTo(file, target, { fit = 'pad', background = '#ffffff', mime = 'image/jpeg', quality = 0.92, name } = {}) {
  if (!isReadable(file.type)) {
    throw new Error(`${file.name} is a ${shortType(file.type)}, which cannot be converted here.`);
  }

  const { bitmap, width, height } = await readImage(file);
  const plan = planExact({ width, height, type: file.type }, target, { fit, mime });

  const canvas = document.createElement('canvas');
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext('2d');

  // PNG and WebP keep transparency, so leave the canvas clear; JPEG cannot, so
  // paint the background or transparent areas come out black.
  if (mime === 'image/jpeg') {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, plan.width, plan.height);
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, plan.offsetX, plan.offsetY, plan.drawWidth, plan.drawHeight);
  bitmap.close?.();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(`${file.name} could not be converted.`))),
      mime,
      // PNG ignores quality; passing it is harmless and keeps one call site.
      quality,
    );
  });

  return {
    file: new File([blob], name ?? file.name, { type: mime }),
    plan,
  };
}

/**
 * Turns one file into something Instagram will take.
 *
 * Returns the original untouched when nothing needs doing — re-encoding a
 * perfectly good JPEG only loses quality and gains nothing.
 *
 * @param {File} file
 * @param {{fit?: 'pad'|'crop', background?: string, quality?: number}} [options]
 * @returns {Promise<{file: File, plan: object, changed: boolean}>}
 */
export async function prepareForInstagram(file, { fit = 'pad', background = '#ffffff', quality = 0.92 } = {}) {
  if (!isReadable(file.type)) {
    throw new Error(`${file.name} is a ${shortType(file.type)}, which cannot be converted here.`);
  }

  const { bitmap, width, height } = await readImage(file);
  const plan = planConversion({ width, height, type: file.type }, { fit });

  if (!plan.needed) {
    bitmap.close?.();
    return { file, plan, changed: false };
  }

  const canvas = document.createElement('canvas');
  canvas.width = plan.width;
  canvas.height = plan.height;

  const ctx = canvas.getContext('2d');
  // JPEG has no transparency, so a PNG with a transparent background would
  // otherwise come out black. Painting the background first is what stops that.
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, plan.width, plan.height);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, plan.offsetX, plan.offsetY, plan.drawWidth, plan.drawHeight);
  bitmap.close?.();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(`${file.name} could not be converted.`))),
      INSTAGRAM.mime,
      quality,
    );
  });

  return {
    file: new File([blob], jpegName(file.name), { type: INSTAGRAM.mime }),
    plan,
    changed: true,
  };
}
